import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { fetchTrending, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { useScrollObserver } from "@/hooks/useScrollObserver";
import InfiniteNewsFeed from "@/components/InfiniteNewsFeed";
import SEO, { buildCategorySchema } from "@/components/SEO";

export default function CategoryPage() {
  const { slug } = useParams();
  const { user, settings } = useAuth();
  const country = (user?.country || "US").toUpperCase();
  const ttlMs = (settings?.refresh_interval_minutes || 15) * 60 * 1000;

  const category = CATEGORIES.find((c) => c.slug === slug) || { slug, label: slug };

  const fetchFn = useCallback(
    (page) => fetchTrending(slug, country, page, 12),
    [slug, country]
  );
  const getCacheKey = useCallback(
    (page) => `news_${slug}_${country}_${page}`,
    [slug, country]
  );

  const feed = useInfiniteFeed({
    fetchFn,
    feedKey: `${slug}_${country}`,
    getCacheKey,
    ttlMs,
  });

  const sentinelRef = useScrollObserver(feed.loadMore);

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6">
      <SEO
        title={`${category.label} AI News`}
        description={`Latest ${category.label.toLowerCase()} headlines aggregated from trusted publishers worldwide. Breaking stories, analysis, and trending coverage.`}
        canonical={`https://trendming.com/category/${slug}`}
        structuredData={[buildCategorySchema(category.label)]}
      />

      <section className="py-10 md:py-14 border-b border-slate-200" data-testid="category-header">
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-2">Category</p>
        <h1 className="font-bold text-4xl md:text-5xl text-slate-900 mt-1">
          {category.label}
        </h1>
        <p className="mt-3 text-slate-500 max-w-2xl leading-relaxed">
          Latest {category.label.toLowerCase()} headlines aggregated from trusted publishers worldwide.
        </p>
      </section>

      <section className="py-10" data-testid="category-grid-section">
        <InfiniteNewsFeed
          feed={feed}
          sentinelRef={sentinelRef}
          testId="category-grid"
        />
      </section>
    </div>
  );
}
